import { isCustomerDisplayId, isCustomerDisplayPublisherKey } from '@washday/utilities';

export interface DisplayIdentity { displayId: string; publisherKey: string }
const currentKey = 'washday.customerDisplay.current';
const archiveKey = (id: string) => `washday.customerDisplay.key.${id}`;

async function verify(identity: DisplayIdentity) {
  if (!isCustomerDisplayId(identity?.displayId) || !isCustomerDisplayPublisherKey(identity?.publisherKey)) return false;
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(identity.publisherKey));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('') === identity.displayId;
}

async function readIdentity(id: string): Promise<DisplayIdentity | null> {
  try {
    const identity = JSON.parse(localStorage.getItem(archiveKey(id)) || 'null') as DisplayIdentity;
    return identity?.displayId === id && await verify(identity) ? identity : null;
  } catch { return null; }
}

let loading: Promise<DisplayIdentity> | null = null;
export async function loadDisplayIdentity(): Promise<DisplayIdentity> {
  if (loading) return loading;
  loading = (async () => {
    const current = localStorage.getItem(currentKey);
    const saved = current && await readIdentity(current);
    if (saved) return saved;
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    const publisherKey = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(publisherKey));
    const displayId = Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, '0')).join('');
    const identity = { displayId, publisherKey };
    localStorage.setItem(archiveKey(displayId), JSON.stringify(identity));
    localStorage.setItem(currentKey, displayId);
    return identity;
  })();
  try { return await loading; } finally { loading = null; }
}

export async function restoreDisplayIdentity(link: string) {
  const url = new URL(link);
  const match = /^\/customer-display\/([a-f0-9]{64})\/?$/.exec(url.pathname);
  if (url.origin !== location.origin || url.search || url.hash || !match) throw new Error('invalid_link');
  const identity = await readIdentity(match[1]);
  if (!identity) throw new Error('missing_publisher_key');
  localStorage.setItem(currentKey, identity.displayId);
  return identity;
}
